import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Copy, CornerUpLeft, Flag, Pencil, SmilePlus, Trash2 } from "lucide-react";
import { useDialogA11y } from "../../hooks/useDialogA11y";
import { readableNameColor } from "../../lib/nameColor";
import { copyToClipboard } from "../../lib/ui";
import type { Msg, ReactionInfo } from "../../types/chat";

type Member = { id: string; name?: string; username?: string; avatarUrl?: string | null; nameColor?: string | null };

/** Shared bottom-sheet shell: dimmed backdrop, slide-up panel, drag handle that dismisses on a downward swipe. */
function BottomSheet({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startY: number; startT: number; dy: number } | null>(null);
  useDialogA11y(true, panelRef, onClose);

  // Keep the page behind from scrolling while the sheet is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onHandleDown = (e: React.PointerEvent) => {
    drag.current = { startY: e.clientY, startT: performance.now(), dy: 0 };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onHandleMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const panel = panelRef.current;
    if (!d || !panel) return;
    // Free downward, friction upward
    d.dy = e.clientY - d.startY;
    panel.style.transition = "none";
    panel.style.transform = `translateY(${d.dy > 0 ? d.dy : d.dy * 0.2}px)`;
  };
  const onHandleUp = () => {
    const d = drag.current;
    const panel = panelRef.current;
    drag.current = null;
    if (!d || !panel) return;
    const velocity = d.dy / Math.max(1, performance.now() - d.startT);
    if (d.dy > 90 || velocity > 0.5) { onClose(); return; }
    panel.style.transition = "transform 200ms var(--ease-out-strong)";
    panel.style.transform = "";
  };

  return createPortal(
    <div className="fixed inset-0 z-[40000] flex items-end" role="presentation">
      <div className="overlay-fade absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="sheet-up relative max-h-[85vh] w-full overflow-y-auto rounded-t-2xl border-t border-white/10 bg-neutral-900 pb-[max(1rem,env(safe-area-inset-bottom))] outline-none"
      >
        <div
          className="flex h-8 cursor-grab touch-none items-center justify-center"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
        >
          <span className="h-1 w-10 rounded-full bg-white/20" />
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function ActionRow({ icon, label, onClick, danger }: { icon: ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-12 w-full items-center gap-4 px-4 text-left text-base transition-colors active:bg-white/[0.06] ${danger ? "text-red-300" : "text-fg"}`}
    >
      <span className={`flex h-5 w-5 items-center justify-center ${danger ? "text-red-300" : "text-fg-2"}`}>{icon}</span>
      {label}
    </button>
  );
}

export type MessageActionSheetProps = {
  msg: Msg;
  mine: boolean;
  quickEmojis: string[];
  onReact: (emoji: string) => void;
  onMoreReactions: () => void;
  onReply: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReport: () => void;
  onClose: () => void;
};

/** Long-press sheet: quick reactions on top, then the message actions. */
export function MessageActionSheet(p: MessageActionSheetProps) {
  const [copied, setCopied] = useState(false);
  const run = (fn: () => void) => () => { fn(); p.onClose(); };
  const copy = async () => {
    const ok = await copyToClipboard(p.msg.content || "");
    if (!ok) { p.onClose(); return; }
    setCopied(true);
    window.setTimeout(p.onClose, 350);
  };
  const emojis = p.quickEmojis.slice(0, 6);
  return (
    <BottomSheet label="Message actions" onClose={p.onClose}>
      <div className="flex items-center gap-2 px-4 pb-3">
        {emojis.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={run(() => p.onReact(emoji))}
            className="flex h-12 min-w-0 flex-1 items-center justify-center rounded-xl bg-white/[0.06] text-2xl transition-colors active:bg-white/[0.12]"
            aria-label={`React with ${emoji}`}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          onClick={run(p.onMoreReactions)}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-fg-2 transition-colors active:bg-white/[0.12]"
          aria-label="More reactions"
        >
          <SmilePlus className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </div>
      <div className="mx-4 overflow-hidden rounded-xl bg-white/[0.04] [&>*+*]:border-t [&>*+*]:border-white/10">
        <ActionRow icon={<CornerUpLeft className="h-5 w-5" strokeWidth={1.5} />} label="Reply" onClick={run(p.onReply)} />
        {p.msg.content ? <ActionRow icon={<Copy className="h-5 w-5" strokeWidth={1.5} />} label={copied ? "Copied" : "Copy text"} onClick={copy} /> : null}
        {p.mine ? <ActionRow icon={<Pencil className="h-5 w-5" strokeWidth={1.5} />} label="Edit message" onClick={run(p.onEdit)} /> : null}
        {p.mine
          ? <ActionRow icon={<Trash2 className="h-5 w-5" strokeWidth={1.5} />} label="Delete message" onClick={run(p.onDelete)} danger />
          : <ActionRow icon={<Flag className="h-5 w-5" strokeWidth={1.5} />} label="Report message" onClick={run(p.onReport)} danger />}
      </div>
    </BottomSheet>
  );
}

export type MessageDetailsSheetProps = {
  msg: Msg;
  authorLabel: string;
  members: Member[];
  reactionUsers: Record<string, { users: ReactionInfo[] }> | undefined;
  onClose: () => void;
};

function fullDate(ts?: string) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString([], { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

/** Single-tap sheet: when it was sent, who reacted with what, and who has seen it. */
export function MessageDetailsSheet({ msg, authorLabel, members, reactionUsers, onClose }: MessageDetailsSheetProps) {
  const reactions = Object.entries(msg.reactions || {});
  const seen = Array.isArray(msg.seenByIds) ? msg.seenByIds : [];
  const nameOf = (id: string) => {
    const m = members.find((mm) => String(mm.id) === String(id));
    return { m, label: m?.name?.trim() || m?.username?.trim() || id.slice(0, 8) };
  };
  return (
    <BottomSheet label="Message details" onClose={onClose}>
      <div className="space-y-4 px-4 pb-2">
        <div>
          <p className="text-meta text-fg-3">Sent by {authorLabel}</p>
          <p className="mt-0.5 text-base text-fg">{fullDate(msg.createdAt)}</p>
          {msg.updatedAt ? <p className="mt-0.5 text-sm text-fg-2">Edited {fullDate(msg.updatedAt)}</p> : null}
        </div>

        <div>
          <p className="text-meta text-fg-3">Reactions</p>
          {reactions.length === 0 ? (
            <p className="mt-1 text-sm text-fg-2">No reactions yet</p>
          ) : (
            <ul className="mt-1 divide-y divide-white/10 overflow-hidden rounded-xl bg-white/[0.04]">
              {reactions.map(([emoji, v]) => {
                const users = reactionUsers?.[emoji]?.users;
                return (
                  <li key={emoji} className="flex items-start gap-3 px-3 py-2.5">
                    <span className="text-xl leading-6">{emoji}</span>
                    <div className="min-w-0 flex-1 text-sm">
                      {users && users.length > 0 ? (
                        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                          {users.map((u, i) => {
                            const { m, label } = nameOf(u.userId);
                            const color = readableNameColor(m?.nameColor);
                            return (
                              <span key={`${u.userId}-${i}`} style={color ? { color } : undefined} className={color ? "" : "text-fg"}>
                                {u.name?.trim() || u.username?.trim() || label}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-fg-2">{v.count} {v.count === 1 ? "person" : "people"}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {seen.length > 0 ? (
          <div>
            <p className="text-meta text-fg-3">Seen by</p>
            <p className="mt-1 text-sm text-fg">{seen.map((id) => nameOf(id).label).join(", ")}</p>
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
}
